/**
 * LocaleContext — the single place that answers "which country, currency,
 * locale and timezone apply right now?"
 *
 *   • Inside a shop: the shop's `country` (and `timezone`) decide. A Spanish
 *     shop formats money as "1.234,56 €", dates as "6 oct 2026", phones as +34,
 *     and sends reminders in Spanish — regardless of the staff member's own
 *     device settings.
 *   • Logged out / no shop yet: visitor region detection (GeoIP cookie,
 *     timezone, browser language) picks sensible defaults.
 *
 * UI *language* is a separate, per-user preference (i18n). We nudge it to the
 * shop's default language the first time a shop loads, unless the user has
 * explicitly chosen a language in Settings.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getCountryConfig, type CountryConfig } from "@/lib/countries";
import { formatDate as baseFormatDate, formatMoney as baseFormatMoney } from "@/lib/locale";
import { detectRegion, getStoredLanguage, toSupportedLanguage, type SupportedLanguage } from "@/lib/region";
import { CALLING_CODES, formatPhoneDisplay, toE164 } from "@/lib/phone";
import {
  formatDateStyle,
  formatWithPattern,
  getDateFnsLocale,
  uses24HourClock,
  type DateStyle,
} from "@/lib/dates";
import type { Locale as DateFnsLocale } from "date-fns";

export interface ShopLocaleRow {
  id: string;
  name: string;
  country: string;
  timezone: string | null;
  phone: string | null;
}

export interface LocaleContextValue {
  /** ISO 3166-1 alpha-2 — "US", "ES" */
  countryCode: string;
  country: CountryConfig;
  /** ISO 4217 — "USD", "EUR" */
  currency: string;
  /** BCP-47 formatting locale for money/dates — follows the UI language when the country supports it */
  locale: string;
  /** Current UI language */
  language: SupportedLanguage;
  /** IANA timezone of the shop (or the visitor's, before a shop exists) */
  timezone: string;
  /** "+34" / "+1" */
  callingCode: string;
  /** Whether the value comes from a real shop row (vs. visitor detection) */
  source: "shop" | "visitor";
  shop: ShopLocaleRow | null;
  formatMoney: (amount: number, opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => string;
  formatMoneyCompact: (amount: number) => string;
  formatDate: (
    date: Date | string,
    opts?: { dateStyle?: "full" | "long" | "medium" | "short"; timeStyle?: "full" | "long" | "medium" | "short" },
  ) => string;
  /** Semantic date-fns formatting in the current locale — see `@/lib/dates` for the style table. */
  fd: (date: Date | string | number | null | undefined, style: DateStyle) => string;
  /** Explicit date-fns pattern, localized month/day names (calendar headers etc.). */
  fdPattern: (date: Date | string | number | null | undefined, pattern: string) => string;
  /** date-fns `Locale` for `<Calendar locale>` / `react-day-picker` (undefined = en-US). */
  dateFnsLocale: DateFnsLocale | undefined;
  /** 24-hour clock (es-ES) vs 12-hour AM/PM (en-US). */
  is24Hour: boolean;
  toE164: (phone: string | null | undefined) => string | null;
  formatPhone: (phone: string | null | undefined) => string;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function visitorTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
  } catch {
    return "America/Chicago";
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { currentShopId } = useAuth();
  const { i18n } = useTranslation();

  const shopQuery = useQuery({
    queryKey: ["shop-locale", currentShopId],
    queryFn: async (): Promise<ShopLocaleRow | null> => {
      if (!currentShopId) return null;
      const { data, error } = await supabase
        .from("shops")
        .select("id, name, country, timezone, phone")
        .eq("id", currentShopId)
        .maybeSingle();
      if (error) throw error;
      return (data as ShopLocaleRow | null) ?? null;
    },
    enabled: !!currentShopId,
    staleTime: 5 * 60 * 1000,
  });

  const shop = shopQuery.data ?? null;
  const language = (toSupportedLanguage(i18n.language) ?? "en-US") as SupportedLanguage;

  // First time a shop loads, follow its default language unless the user chose one explicitly.
  useEffect(() => {
    if (!shop) return;
    if (getStoredLanguage()) return;
    const shopLanguage = toSupportedLanguage(getCountryConfig(shop.country).defaultLocale);
    if (shopLanguage && shopLanguage !== toSupportedLanguage(i18n.language)) {
      i18n.changeLanguage(shopLanguage);
    }
    // Only when the shop (not the language) changes — we don't want to fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id]);

  const value = useMemo<LocaleContextValue>(() => {
    const visitor = shop ? null : detectRegion();
    const countryCode = (shop?.country || visitor?.countryCode || "US").toUpperCase();
    const country = getCountryConfig(countryCode);
    const currency = country.currency;
    // Format in the UI language when the country supports it (an English-speaking
    // owner of a Spanish shop still sees "1.234,56 €" — currency placement follows
    // the country, digits follow the language).
    const locale = country.locales.includes(language) ? language : country.defaultLocale;
    const timezone = shop?.timezone || (shop ? country.defaultTimezone : visitorTimezone());
    const callingCode = `+${CALLING_CODES[countryCode] ?? "1"}`;

    return {
      countryCode,
      country,
      currency,
      locale,
      language,
      timezone,
      callingCode,
      source: shop ? "shop" : "visitor",
      shop,
      formatMoney: (amount, opts) => baseFormatMoney(amount, { currency, locale, ...opts }),
      formatMoneyCompact: (amount) =>
        baseFormatMoney(amount, { currency, locale, minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      formatDate: (date, opts) => baseFormatDate(date, { locale, timezone, ...opts }),
      fd: (date, style) => formatDateStyle(date, style, locale),
      fdPattern: (date, pattern) => formatWithPattern(date, pattern, locale),
      dateFnsLocale: getDateFnsLocale(locale),
      is24Hour: uses24HourClock(locale),
      toE164: (phone) => toE164(phone, countryCode),
      formatPhone: (phone) => formatPhoneDisplay(phone, countryCode),
    };
  }, [shop, language]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return ctx;
}

/** Shorthand for components that only format money. */
export function useMoney() {
  const { formatMoney, formatMoneyCompact, currency, locale } = useLocale();
  return { formatMoney, formatMoneyCompact, currency, locale };
}

/**
 * Shorthand for components that format dates with date-fns.
 *
 *   const { fd, dateFnsLocale } = useDateFormat();
 *   fd(appt.scheduled_at, "date")      // "Oct 6, 2026" | "6 oct 2026"
 *   fd(appt.scheduled_at, "time")      // "2:30 PM"     | "14:30"
 *   <Calendar locale={dateFnsLocale} />
 */
export function useDateFormat() {
  const { fd, fdPattern, dateFnsLocale, is24Hour, locale } = useLocale();
  return { fd, fdPattern, dateFnsLocale, is24Hour, locale };
}

/**
 * Currency ShopLedger bills this shop's subscription in: EUR for eurozone
 * shops (or eurozone visitors before a shop exists), USD for everyone else.
 * Mirrors supabase/functions/_shared/prices.ts → currencyForCountry().
 *
 *   const { billingCurrency, formatPlanPrice } = useBillingMoney();
 *   formatPlanPrice(19)      // "$19" | "19 €"
 *   formatPlanPrice(205.2)   // "$205.20" | "205,20 €"
 */
export function useBillingMoney() {
  const { currency, locale } = useLocale();
  const billingCurrency: "USD" | "EUR" = currency === "EUR" ? "EUR" : "USD";
  const formatPlanPrice = (amount: number) =>
    baseFormatMoney(amount, {
      currency: billingCurrency,
      locale,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  return { billingCurrency, formatPlanPrice };
}
